import type { FormatBundle } from '../format/bundle';

/**
 * The formatter bundle, reachable from outside React render.
 *
 * Plenty of code that has to format is not a component and cannot call a hook: table column
 * definitions, filename builders, CSV writers, the mappers that turn a response into rows. Without
 * this they would each have to be handed preferences by whichever component happens to own them,
 * which is the prop-drilling the provider exists to avoid.
 *
 * The provider publishes here during its own render rather than from an effect. Child effects run
 * before parent effects, so a component that formats in its first effect would find nothing
 * published if this waited for the provider's. Writing during render is a side effect, but an
 * idempotent one — the same bundle for the same preferences — so a render that React discards
 * cannot leave anything wrong behind.
 */

let current: FormatBundle | undefined;

/**
 * Whether the published bundle formats, as opposed to the inert one the rollout flag hands out.
 *
 * Held next to the bundle rather than read off it because the two bundles have the same type by
 * construction, which is what makes the flag safe to flip. Nothing about a bundle says which one
 * it is.
 */
let currentFormats = false;

/** Called by the provider. Not part of the package's public surface. */
export const publishFormatSnapshot = (
  bundle: FormatBundle,
  formats: boolean
): void => {
  current = bundle;
  currentFormats = formats;
};

/**
 * Called by the provider on unmount.
 *
 * It clears only its own bundle, so a provider unmounting after another has taken over leaves the
 * live one in place.
 */
export const clearFormatSnapshot = (bundle: FormatBundle): void => {
  if (current === bundle) {
    current = undefined;
    currentFormats = false;
  }
};

/**
 * The bundle if a provider has rendered and the rollout flag is on, and `undefined` otherwise.
 *
 * For the one caller that has somewhere else to go: a legacy utility being re-pointed at the core
 * while its own pre-core implementation is still present. Those have to keep working in tests and
 * in any tree that has not mounted a provider yet, so for them an absent provider is a branch
 * rather than a fault.
 *
 * A disabled provider counts as absent for the same reason. Such a caller's fallback *is* the
 * pre-rollout behaviour, which is what the flag has to restore when it goes off; handing over the
 * inert bundle instead would satisfy the optional chain and suppress that fallback, turning the
 * flag from a way back into the old output into a way to lose it.
 *
 * Anything without a fallback should call {@link getFormatSnapshot} instead and get the
 * diagnostic. Reaching for this one to silence that error converts a wiring bug into values
 * rendered in the wrong units, which is the harder defect to notice.
 */
export const tryGetFormatSnapshot = (): FormatBundle | undefined =>
  currentFormats ? current : undefined;

/**
 * The same formatters `useFormat()` returns, for code that cannot use a hook.
 *
 * Answers with whichever bundle is mounted, the inert one included, which is where it parts company
 * with {@link tryGetFormatSnapshot}. A caller here has no fallback to fall back to, so withholding
 * the bundle while the flag is off would leave it with nothing rather than with its own old output.
 *
 * @returns The bundle for the currently mounted provider.
 * @throws If no provider has rendered yet.
 */
export const getFormatSnapshot = (): FormatBundle => {
  if (!current) {
    throw new Error(
      'getFormatSnapshot() was called before any <LocalizationProvider> had rendered. It reads the ' +
        'same formatters as useFormat(), so it needs a provider mounted near the root of the app ' +
        'shell. If this is a column definition or a mapper, call it inside the function that builds ' +
        'the columns rather than when the module is evaluated, so it runs after the app has started.'
    );
  }

  return current;
};
